import { useEffect, useState } from "react";
import { api } from "../api/client";
import { PeriodPicker } from "../components/PeriodPicker";
import {
  ExportInfo,
  ExportListResponse,
  KpiResponse,
  MeResponse,
  TemplatesResponse,
} from "../types";

interface Props {
  me: MeResponse;
}

export function AnalyticsPage({ me: _me }: Props) {
  const [tplData, setTplData] = useState<TemplatesResponse | null>(null);
  const [kpi, setKpi] = useState<KpiResponse | null>(null);
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState<string | null>(null);
  const [customEnd, setCustomEnd] = useState<string | null>(null);
  const [history, setHistory] = useState<ExportInfo[]>([]);
  const [running, setRunning] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const periodParams = (): Record<string, unknown> => {
    const p: Record<string, unknown> = { period_key: period };
    if (period === "custom") {
      p.custom = customStart && customEnd ? [customStart, customEnd] : null;
    }
    return p;
  };

  useEffect(() => {
    api.get<TemplatesResponse>("/api/analytics/templates").then(setTplData)
      .catch((e) => setError(String(e)));
    refreshHistory();
  }, []);

  // KPI перезапрашивается при КАЖДОЙ смене периода.
  useEffect(() => {
    const qs = new URLSearchParams({ periodKey: period });
    if (period === "custom" && customStart && customEnd) {
      qs.set("customStart", customStart);
      qs.set("customEnd", customEnd);
    } else if (period === "custom") {
      return; // ждём, пока оператор задаст обе даты
    }
    setKpi(null);
    api.get<KpiResponse>(`/api/analytics/kpi?${qs}`).then(setKpi)
      .catch((e) => setError(String(e)));
  }, [period, customStart, customEnd]);

  const refreshHistory = async () => {
    try {
      const r = await api.get<ExportListResponse>(
        "/api/analytics/exports?onlyMine=true&limit=20",
      );
      setHistory(r.exports);
    } catch {
      /* ignore */
    }
  };

  const runTemplate = async (kind: string) => {
    setRunning(kind);
    setError(null);
    try {
      // Grant-resolver сам соберёт xlsx и запишет его в analytics_exports
      // (template_*-функции внутри вызывают save_export). Отдельный POST
      // на /templates/{kind}/run не нужен.
      await api.download(
        "analytics_template",
        { kind, ...periodParams() },
        `${kind}.xlsx`,
      );
      refreshHistory();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRunning(null);
    }
  };

  return (
    <div>
      <h1 className="text-xl font-semibold mb-3">📊 Аналитика</h1>

      <div className="mb-4">
        <label className="text-xs text-tg-hint">Период</label>
        <PeriodPicker
          value={period}
          onChange={setPeriod}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={(s, e) => {
            setCustomStart(s);
            setCustomEnd(e);
          }}
        />
      </div>

      {kpi && (
        <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
          <div className="text-xs text-tg-hint">KPI · {kpi.period_label}</div>
          <div className="mt-1 text-2xl font-semibold">
            {fmt(kpi.totals.month_rub_volume)} ₽
          </div>
          <div className="text-sm text-tg-hint">
            {kpi.totals.month_deals} заявок · вознаграждение{" "}
            {fmt(kpi.totals.month_fee_total_rub)} ₽
            {kpi.avg_fee_pct != null
              ? ` · ${kpi.avg_fee_pct.toFixed(4)} %`
              : ""}
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <KpiTile label="Топ-компания" value={kpi.top_company || "—"} />
            <KpiTile label="Топ-контрагент" value={kpi.top_counterparty || "—"} />
            <KpiTile label="Топ-валюта" value={kpi.top_currency || "—"} />
          </div>
          {kpi.weekly_trend.length > 0 && (
            <div className="mt-4">
              <div className="text-xs text-tg-hint mb-1">Тренд по неделям (RUB)</div>
              <Sparkline values={kpi.weekly_trend.map((w) => w.rub_volume)} />
              <div className="mt-1 grid grid-cols-4 gap-1 text-[10px] text-tg-hint">
                {kpi.weekly_trend.map((w) => (
                  <div key={w.week} className="text-center">
                    {w.week.slice(-3)}
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">
          {error}
        </div>
      )}

      <CompareSection />

      <h2 className="text-sm font-medium text-tg-sectionHeader uppercase mb-2">
        Шаблоны
      </h2>
      <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
        {tplData?.templates.map((t) => (
          <div key={t.kind} className="px-4 py-3">
            <div className="flex justify-between items-start gap-2">
              <div>
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-xs text-tg-hint mt-0.5">{t.description}</div>
              </div>
              <button
                onClick={() => runTemplate(t.kind)}
                disabled={running !== null || t.kind === "counterparty_seasonality"}
                className="shrink-0 px-3 py-1.5 rounded-lg bg-tg-button text-tg-buttonText text-sm disabled:opacity-50"
              >
                {running === t.kind ? "…" : "▶"}
              </button>
            </div>
          </div>
        ))}
      </div>

      {tplData && (
        <ConstructorSection
          tplData={tplData}
          period={period}
          customStart={customStart}
          customEnd={customEnd}
          onRun={refreshHistory}
        />
      )}

      <SubscriptionsSection />

      {history.length > 0 && (
        <>
          <h2 className="text-sm font-medium text-tg-sectionHeader uppercase mt-5 mb-2">
            История моих выгрузок
          </h2>
          <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
            {history.map((e) => (
              <div key={e.id} className="px-4 py-2.5 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm truncate">{e.title || e.kind}</div>
                  <div className="text-[10px] text-tg-hint">
                    {new Date(e.created_at).toLocaleString("ru-RU")}
                  </div>
                </div>
                <button
                  onClick={() =>
                    api.download(
                      "analytics_export",
                      { export_id: e.id },
                      `${e.kind}.xlsx`,
                    )
                  }
                  className="shrink-0 text-tg-accentText text-sm"
                >
                  📥
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ===================== Сравнение периодов =====================

const COMPARE_PERIODS: Array<[string, string]> = [
  ["today", "Сегодня"],
  ["yesterday", "Вчера"],
  ["this_week", "Эта неделя"],
  ["last_week", "Прошлая неделя"],
  ["this_month", "Этот месяц"],
  ["last_month", "Прошлый месяц"],
  ["this_quarter", "Этот квартал"],
];

interface CompareTotals {
  period_key: string;
  label: string;
  rub_volume: number;
  deals: number;
  fee_total_rub: number;
}
interface CompareResp {
  a: CompareTotals;
  b: CompareTotals;
  delta_rub_volume_pct: number | null;
  delta_deals_pct: number | null;
  delta_fee_pct: number | null;
}

function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct == null)
    return <span className="text-tg-hint text-xs">н/д</span>;
  const up = pct > 0;
  const flat = pct === 0;
  return (
    <span
      className="text-xs font-medium"
      style={{ color: flat ? "#64748b" : up ? "#16a34a" : "#dc2626" }}
    >
      {flat ? "—" : up ? "▲" : "▼"} {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function CompareSection() {
  const [a, setA] = useState("this_month");
  const [b, setB] = useState("last_month");
  const [data, setData] = useState<CompareResp | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setData(null);
    setErr(null);
    api
      .get<CompareResp>(`/api/analytics/compare?a=${a}&b=${b}`)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [a, b]);

  const selCls =
    "px-2 py-1.5 rounded-lg bg-tg-secondaryBg text-tg-text text-sm";

  return (
    <section className="bg-tg-sectionBg rounded-xl p-4 mb-4">
      <div className="text-sm font-medium mb-2">Сравнение периодов</div>
      <div className="flex items-center gap-2 mb-3">
        <select
          value={a}
          onChange={(e) => setA(e.target.value)}
          className={selCls}
        >
          {COMPARE_PERIODS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <span className="text-tg-hint text-xs">к</span>
        <select
          value={b}
          onChange={(e) => setB(e.target.value)}
          className={selCls}
        >
          {COMPARE_PERIODS.map(([k, l]) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
      </div>

      {err && <div className="text-xs text-red-700">{err}</div>}
      {!data && !err && (
        <div className="text-xs text-tg-hint">Загрузка…</div>
      )}
      {data && (
        <div className="space-y-2">
          <CompareRow
            label="Объём, ₽"
            av={fmt(data.a.rub_volume)}
            bv={fmt(data.b.rub_volume)}
            pct={data.delta_rub_volume_pct}
          />
          <CompareRow
            label="Заявок"
            av={String(data.a.deals)}
            bv={String(data.b.deals)}
            pct={data.delta_deals_pct}
          />
          <CompareRow
            label="Вознаграждение, ₽"
            av={fmt(data.a.fee_total_rub)}
            bv={fmt(data.b.fee_total_rub)}
            pct={data.delta_fee_pct}
          />
          <div className="text-[11px] text-tg-hint pt-1">
            {data.a.label} · к · {data.b.label}
          </div>
        </div>
      )}
    </section>
  );
}

function CompareRow({
  label,
  av,
  bv,
  pct,
}: {
  label: string;
  av: string;
  bv: string;
  pct: number | null;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2 text-sm">
      <span className="text-tg-hint">{label}</span>
      <span className="flex items-baseline gap-2">
        <span className="font-semibold tabular-nums">{av}</span>
        <span className="text-tg-hint text-xs tabular-nums">/ {bv}</span>
        <DeltaBadge pct={pct} />
      </span>
    </div>
  );
}

function KpiTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-tg-secondaryBg rounded-lg p-2 min-w-0">
      <div className="text-tg-hint">{label}</div>
      <div className="font-medium truncate">{value}</div>
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  const w = 100;
  const h = 32;
  const step = w / Math.max(values.length - 1, 1);
  const points = values
    .map((v, i) => `${i * step},${h - (v / max) * h}`)
    .join(" ");
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-8"
      preserveAspectRatio="none"
    >
      <polyline
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        points={points}
        className="text-tg-accentText"
      />
    </svg>
  );
}

function fmt(n: number): string {
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n);
}

// ===================== Подписки на отчёты =====================

interface SubKind {
  kind: string;
  label: string;
}
interface Subscription {
  id: number;
  kind: string;
  kind_label: string;
  cadence: string;
  last_sent_at: string | null;
}
interface SubsResponse {
  kinds: SubKind[];
  subscriptions: Subscription[];
}

function SubscriptionsSection() {
  const [data, setData] = useState<SubsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = async () => {
    try {
      setData(await api.get<SubsResponse>("/api/subscriptions"));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const subByKind = (kind: string) =>
    data?.subscriptions.find((s) => s.kind === kind) || null;

  const toggle = async (kind: string) => {
    const existing = subByKind(kind);
    setBusy(true);
    setErr(null);
    try {
      if (existing) {
        await api.del(`/api/subscriptions/${existing.id}`);
      } else {
        await api.post("/api/subscriptions", { kind });
      }
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 className="text-sm font-medium text-tg-sectionHeader uppercase mt-5 mb-2">
        📨 Подписки на отчёты
      </h2>
      <div className="text-xs text-tg-hint mb-2">
        Отмеченные отчёты приходят авторассылкой каждый понедельник в
        10:00.
      </div>
      {err && <div className="text-xs text-red-700 mb-2">{err}</div>}
      <div className="bg-tg-sectionBg rounded-xl divide-y divide-tg-secondaryBg overflow-hidden">
        {data?.kinds.map((k) => {
          const sub = subByKind(k.kind);
          return (
            <div
              key={k.kind}
              className="px-4 py-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <div className="text-sm">{k.label}</div>
                {sub?.last_sent_at && (
                  <div className="text-[10px] text-tg-hint">
                    последняя рассылка:{" "}
                    {new Date(sub.last_sent_at).toLocaleDateString("ru-RU")}
                  </div>
                )}
              </div>
              <button
                disabled={busy}
                onClick={() => toggle(k.kind)}
                className={
                  "shrink-0 px-3 py-1.5 rounded-lg text-xs disabled:opacity-50 " +
                  (sub
                    ? "bg-tg-secondaryBg text-tg-text"
                    : "bg-tg-button text-tg-buttonText")
                }
              >
                {sub ? "✓ Подписан" : "Подписаться"}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ===================== Конструктор отчётов =====================

interface ConstructorProps {
  tplData: TemplatesResponse;
  period: string;
  customStart: string | null;
  customEnd: string | null;
  onRun: () => void;
}

const SLICE_LABELS: Record<string, string> = {
  total: "Один столбец «Итого»",
  daily: "По дням внутри периода",
  cumulative: "Накопительно по дням",
};

function ConstructorSection({
  tplData, period, customStart, customEnd, onRun,
}: ConstructorProps) {
  const metrics = Object.entries(tplData.metrics);
  const groups = Object.entries(tplData.group_keys);

  const [metric, setMetric] = useState<string>(metrics[0]?.[0] || "rub_volume");
  const [groupX, setGroupX] = useState<string>(groups[0]?.[0] || "company");
  const [groupY, setGroupY] = useState<string>("");
  const [slice, setSlice] = useState<string>("total");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setErr(null);
    try {
      const params: Record<string, unknown> = {
        period_key: period,
        metric,
        group_x: groupX,
        slice_mode: slice,
      };
      if (period === "custom" && customStart && customEnd) {
        params.custom = [customStart, customEnd];
      }
      if (groupY) params.group_y = groupY;
      // Resolver сам построит отчёт и запишет в analytics_exports.
      await api.download("analytics_custom", params, "custom.xlsx");
      onRun();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <h2 className="text-sm font-medium text-tg-sectionHeader uppercase mt-5 mb-2">
        🛠 Конструктор отчёта
      </h2>
      <div className="bg-tg-sectionBg rounded-xl p-4 space-y-3">
        <div>
          <label className="text-xs text-tg-hint">Метрика</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            {metrics.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-tg-hint">Группировка (по оси X)</label>
          <select
            value={groupX}
            onChange={(e) => setGroupX(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            {groups.map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-tg-hint">Вторая группировка (опц.)</label>
          <select
            value={groupY}
            onChange={(e) => setGroupY(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm"
          >
            <option value="">— нет —</option>
            {groups.filter(([k]) => k !== groupX).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs text-tg-hint">Срез</label>
          <select
            value={slice}
            onChange={(e) => setSlice(e.target.value)}
            disabled={!!groupY}
            className="w-full px-3 py-2 rounded-lg bg-tg-secondaryBg text-tg-text text-sm disabled:opacity-50"
          >
            {Object.entries(SLICE_LABELS).map(([k, label]) => (
              <option key={k} value={k}>{label}</option>
            ))}
          </select>
          {!!groupY && (
            <div className="text-[11px] text-tg-hint mt-1">
              При двойной группировке срез не применяется.
            </div>
          )}
        </div>

        {err && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            {err}
          </div>
        )}

        <button
          onClick={run}
          disabled={busy}
          className="w-full px-3 py-2.5 rounded-lg bg-tg-button text-tg-buttonText font-medium disabled:opacity-50"
        >
          {busy ? "Считаю и собираю xlsx…" : "📥 Сформировать xlsx"}
        </button>
      </div>
    </>
  );
}
